import type { OrderView } from '../../shared/types';
import { formatLocalMinor, formatUsd } from './currency';

/**
 * Pure delivery-link assembly helpers, shared by the reception terminal and the
 * dedicated delivery staff page. Kept free of browser globals so the string
 * assembly is unit-testable in the Node vitest environment.
 */

/** Builds the public delivery-page URL for a raw one-time token. */
export function deliveryLinkUrl(origin: string, rawToken: string): string {
  return `${origin}/delivery/${encodeURIComponent(rawToken)}`;
}

/** Strips a phone number to digits for the `wa.me` destination. */
export function phoneForWhatsapp(phone: string): string {
  return phone.replace(/\D/g, '');
}

/** Order summary message used to confirm receipt over WhatsApp (reception). */
export function whatsappMessage(order: OrderView): string {
  const itemLines = order.items.flatMap((item) => {
    const excluded = item.ingredients.filter((ingredient) => ingredient.excluded).map((ingredient) => ingredient.name);
    const lines = [
      `• ${item.quantity}× ${item.productName}${item.variantName ? ` · ${item.variantName}` : ''}${excluded.length ? ` (sin ${excluded.join(', ')})` : ''}`,
    ];
    if (item.options.length) {
      lines.push(`  Opciones: ${item.options.map((option) => `${option.quantity > 1 ? `${option.quantity}× ` : ''}${option.optionName}`).join(', ')}`);
    }
    for (const component of item.components) {
      const componentExcluded = component.excludedIngredients.map((ingredient) => ingredient.name);
      lines.push(`  ${component.componentName}: ${component.quantity}× ${component.productName}${component.variantName ? ` · ${component.variantName}` : ''}${componentExcluded.length ? ` (sin ${componentExcluded.join(', ')})` : ''}`);
      if (component.options.length) {
        lines.push(`    Opciones: ${component.options.map((option) => `${option.quantity > 1 ? `${option.quantity}× ` : ''}${option.optionName}`).join(', ')}`);
      }
    }
    return lines;
  });
  return [
    `Hola ${order.customerName}, recibimos tu pedido #${String(order.correlation).padStart(2, '0')}.`,
    order.deliveryAddress ? `Dirección: ${order.deliveryAddress}` : null,
    order.confirmedMapsUrl ? `Ubicación confirmada: ${order.confirmedMapsUrl}` : null,
    ...itemLines,
    `Total: ${formatUsd(order.totalUsdCents)} / ${formatLocalMinor(order.totalLocalMinor, order.localCurrency, order.currencyDecimals, order.currencySymbol)}`,
    'Confírmanos por este medio para comenzar la preparación.',
  ].filter(Boolean).join('\n');
}

/**
 * WhatsApp URL that includes the single-use delivery link alongside the order
 * summary, for the delivery staff page. The caller passes the already-captured
 * link (see useDeliveryLinks) so the raw token is never re-fetched.
 */
export function deliveryWhatsAppUrl(order: OrderView, link: string): string {
  const message = `${whatsappMessage(order)}\nEnlace de reparto: ${link}`;
  return `https://wa.me/${phoneForWhatsapp(order.customerPhone)}?text=${encodeURIComponent(message)}`;
}

/** True for delivery orders that are ready to be handed to a courier. */
export function isReadyDeliveryOrder(order: OrderView): boolean {
  return order.deliveryType === 'delivery' && order.fulfillmentStage === 'ready';
}
